import { useEffect, useState } from 'react';
import { useTranslations } from 'next-intl';
import { format } from '@monofront/business/utils';
import QuestionOutline from '@monofront/design-system/visuals/Icons/Old/QuestionOutline';
import Tag from '@monofront/design-system/components/Tag';
import { trackEvent } from '@monofront/tracking';
import useSubscriptionPrice from '@hooks/useSubscriptionPrice';
import ModalSubscriptionReinsurance from '@components/ModalSubscriptionReinsurance';
import ResponsiveImage from '@monofront/design-system/components/ResponsiveImage';
import useLocalizedRoutes from '@hooks/i18n/useLocalizedRoutes';
import { RoutesEnum } from '@monofront/types';

interface IShoppingCartMembershipProductRowProps {
  isSideShoppingCart?: boolean;
  isImageLarge?: boolean;
}

const ShoppingCartMembershipProductRow: React.FC<
  IShoppingCartMembershipProductRowProps
> = ({ isSideShoppingCart = false, isImageLarge = false }) => {
  const { annualSubscriptionPrice } = useSubscriptionPrice();
  const tCart = useTranslations('Cart');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { getPath } = useLocalizedRoutes();

  const windowWidth = typeof window !== 'undefined' ? window.innerWidth : 0;

  const imageSize = windowWidth < 800 ? 48 : isImageLarge ? 56 : 48;

  useEffect(() => {
    if (!isModalOpen) return;

    trackEvent({
      type: 'LFOpenMembershipModal',
      data: {
        origin: 'horizontal_product_card',
      },
    });
  }, [isModalOpen]);

  return (
    <>
      <div className="container">
        <div className="cart-item-container">
          <div
            className="img tw-cursor-pointer max-phone:tw-hidden"
            onClick={() => setIsModalOpen(true)}
            aria-hidden
          >
            <ResponsiveImage
              src={getPath(RoutesEnum.MAAP_CARD_IMAGE)}
              layout="responsive"
              objectFit="cover"
              width={imageSize}
              height={imageSize}
            />
          </div>
          <div className="content">
            <div className="infos">
              <p className="vendor">{tCart('MembershipProductCart.brand')}</p>
              <div
                className="title tw-cursor-pointer"
                onClick={() => setIsModalOpen(true)}
              >
                {tCart('MembershipProductCart.productName')}
              </div>
              <p className="product-info">
                <span className="weight">
                  {tCart('MembershipProductCart.reinsurance1')}
                </span>
                <span className="separator" />
                <span className="price-per-unit">
                  {tCart('MembershipProductCart.reinsurance2')}
                </span>
              </p>
            </div>
            <div className="price tw-items-center tw-gap-x-2 max-phone:tw-w-28 max-phone:tw-flex-col max-phone:tw-items-start max-phone:tw-gap-y-1">
              <span className="member-price tw-text-black-1000">
                {tCart('MembershipProductCart.price')}
              </span>
              <div className="tw-flex tw-items-center tw-space-x-2 max-phone:tw-whitespace-nowrap">
                <span className="discount-percent tw-text-center">
                  <Tag variant="secondary">
                    {tCart('MembershipProductCart.flag')}
                  </Tag>
                </span>
                <span className="not-member-price">
                  {tCart('MembershipProductCart.nextPrice', {
                    subscriptionPrice: format(annualSubscriptionPrice, {
                      minimumFractionDigits: 2,
                    }),
                  })}
                </span>
              </div>
            </div>
            <div className="wish-list-button">
              <button
                type="button"
                className="tw-lf-button tw-lf-button-clear desktop:tw-p-2"
                onClick={() => setIsModalOpen(true)}
                aria-label={tCart('MembershipProductCart.moreInformations')}
              >
                <QuestionOutline width={24} height={24} aria-hidden />
              </button>
            </div>
            {!isSideShoppingCart && (
              <>
                <div className="add-to-cart-button"></div>
                <div className="subtotal">
                  <p className="subtotal-p tw-text-black-1000">
                    {tCart('MembershipProductCart.price')}
                  </p>
                </div>
              </>
            )}
          </div>
        </div>
        <ModalSubscriptionReinsurance
          isOpen={isModalOpen}
          closeModal={() => setIsModalOpen(false)}
        />
      </div>

      <style jsx>{`
        .container {
          background-color: var(--white);
          transition: 0.3s;
        }

        .container:last-child {
          border-bottom: none;
        }

        .cart-item-container {
          display: flex;
          align-items: flex-start;
          padding: var(--space-4) 0;
        }

        .img,
        .img :global(img) {
          top: 0;
          width: ${isImageLarge ? '56' : '48'}px;
          height: ${isImageLarge ? '56' : '48'}px;
          min-width: ${isImageLarge ? '56' : '48'}px;
          min-height: ${isImageLarge ? '56' : '48'}px;
        }

        .content {
          display: grid;
          grid-template-columns: 1fr;
          grid-template-rows: 1fr;
          grid-template-areas: ${isSideShoppingCart
            ? '"infos infos wish" "price price price"'
            : '"infos wish quantity subtotal"  "price wish quantity subtotal"'};

          grid-column-gap: ${isSideShoppingCart
            ? 'var(--space-6)'
            : 'var(--space-6)'};
          width: 100%;
          align-items: ${isSideShoppingCart ? 'flex-start' : 'center'};
          margin-left: var(--space-4);
          position: relative;
        }

        .infos {
          margin-bottom: var(--space-2);
          grid-area: infos;
        }

        .infos > *:not(:last-child) {
          margin-bottom: var(--space-2);
        }

        .vendor {
          color: var(--clr-grey-1);
          font-size: var(--font-size-1-5);
        }

        .price-per-unit {
          display: inline;
          font-size: var(--font-size-1);
        }

        .weight {
          margin-right: var(--space-2);
          font-weight: var(--font-weight-bold);
        }

        .weight,
        .vendor,
        .not-member-price,
        .price-per-unit {
          font-size: var(--font-size-1-5);
        }

        .vendor,
        .weight,
        .price-per-unit {
          color: var(--clr-grey-1);
        }

        .price {
          display: flex;
          grid-area: price;
          align-self: ${isSideShoppingCart ? 'flex-end' : 'center'};
        }

        .member-price {
          font-size: var(--font-size-2-5);
          font-family: var(--font-family-1);
          white-space: nowrap;
        }

        .wish-list-button {
          grid-area: wish;
        }

        .add-to-cart-button {
          grid-area: quantity;
          min-width: 116px;
          max-height: ${isSideShoppingCart ? '30px' : 'initial'};
          transform: ${isSideShoppingCart ? 'translateY(-10px)' : 'initial'};
          align-self: ${isSideShoppingCart ? 'flex-end' : 'initial'};
        }

        .subtotal {
          min-width: 75px;
          grid-area: subtotal;
          justify-self: end;
          text-align: right;
        }

        .subtotal-p {
          font-size: var(--font-size-3);
          font-family: var(--font-family-1);
        }

        .wish-list-button {
          justify-self: ${isSideShoppingCart ? 'end' : 'initial'};
          align-self: ${isSideShoppingCart ? 'baseline' : 'initial'};
          transform: ${isSideShoppingCart ? 'translateY(-8px)' : 'initial'};
        }

        .separator {
          width: 1px;
          height: 14px;
          background-color: var(--clr-tertiary-2);
          margin-right: var(--space-2);
        }

        .product-info {
          display: flex;
          align-items: center;
        }

        .title {
          font-size: var(--font-size-1-5);
        }

        @media (max-width: 800px) {
          .subtotal {
            display: none;
          }

          .content {
            grid-column-gap: var(--space-4);
            grid-template-areas:
              'infos infos wish'
              'price price price';
          }

          .wish-list-button {
            justify-self: end;
            align-self: baseline;
          }

          .add-to-cart-button {
            display: none;
          }

          .content {
            padding: var(--space-1) 0;
          }

          .title {
            width: 100%;
          }

          .vendor {
            display: block;
          }

          .img,
          .img :global(img) {
            top: 0;
            width: 48px;
            height: 48px;
            min-width: 48px;
            min-height: 48px;
          }
        }

        @media (min-width: 1079px) and (max-width: 1160px) {
          .add-to-cart-button {
            display: none;
          }
        }
      `}</style>
    </>
  );
};

export default ShoppingCartMembershipProductRow;
